/**
 * `SpecTable` — the 8.5ch spec grid. `docs/design-direction.md` §5.2, §8.1, §8.6.
 *
 * Mono caps `--fs-11` labels in an 8.5ch gutter on the left, mono `--fs-13` values
 * right-aligned: the catalogue plate on Now Playing (`BL-0421 / SIDE A / 45 RPM /
 * BPM 92`), the `RIDDIM / LABEL / YEAR / PRESS / ADDED` block under its downbeat
 * rule, the sleeve back on My Crate, the amp-faceplate strip on an artist page.
 *
 * **It omits what it does not have.** A row whose value is `null`, `undefined`, an
 * empty or whitespace-only string, or a non-finite number is not rendered at all —
 * there is no em-dash placeholder, no `n/a`, no `—`, no `unknown` (§8.1, §12.6). The
 * channel's metadata is uneven, and the design's answer to that is silence, not
 * filler: a plate with three facts reads as a plate with three facts, while a plate
 * with three facts and two dashes reads as broken.
 *
 * `0` is a value and prints. So does the string `'0'`. Only genuinely absent data is
 * dropped, because "we do not know the year" and "the year is 0" are different
 * statements and only one of them is missing.
 *
 * Rows may be given either as an ordered array — the form to prefer, because it puts
 * the print order in the caller's hands — or as a plain record, which is what the
 * data usually arrives as (`{ year: null, bpm: 92 }`). A record prints in its own key
 * order with keys upper-cased into the label tier.
 *
 * The markup is a `<dl>`: these are term/definition pairs, and a screen reader should
 * read "BPM, 92" rather than two unrelated cells. Cyrillic values (a label name, a
 * riddim) keep the mono tier but lose its tracking — tracked uppercase Cyrillic is
 * banned (§4.3, §12.16).
 */

import { Fragment } from 'react';
import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './primitives.module.css';
import { cx } from './Rule';

/** Anything a spec row can hold. `null`/`undefined`/`''` mean "omit the row". */
export type SpecValue = string | number | null | undefined;

export interface SpecRow {
  /** Label printed in the 8.5ch gutter, in the mono caps tier. */
  readonly label: string;
  readonly value: SpecValue;
}

export interface SpecTableProps {
  /** Ordered rows, or a record printed in key order with upper-cased labels. */
  readonly rows: readonly SpecRow[] | Readonly<Record<string, SpecValue>>;
  readonly className?: string | undefined;
}

/** True when a value carries no information and its row must not be printed. */
export function isMissing(value: SpecValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'number') return !Number.isFinite(value);
  return value.trim() === '';
}

/* A hand-written guard, because `Array.isArray` widens a `readonly T[]` union member
   to `any[]` and the narrowing would leak `any` into the rows. */
function isRowArray(rows: SpecTableProps['rows']): rows is readonly SpecRow[] {
  return Array.isArray(rows);
}

function toRows(rows: SpecTableProps['rows']): readonly SpecRow[] {
  if (isRowArray(rows)) return rows;
  return Object.entries(rows).map(([label, value]) => ({ label: label.toUpperCase(), value }));
}

export function SpecTable({ rows, className }: SpecTableProps): JSX.Element | null {
  const printable = toRows(rows).filter((row) => !isMissing(row.value));

  /* An empty spec table is not an empty state: it is nothing at all. The screen's own
     empty state (§8.7) occupies the grid instead — one ruled line, one mono line. */
  if (printable.length === 0) return null;

  return (
    <dl className={cx(styles.spec, className)} data-spec="true">
      {printable.map((row) => {
        const value = String(row.value);
        return (
          /* `dt` and `dd` are the grid's own children — an intervening wrapper would
             become the grid item and break the 8.5ch gutter. */
          <Fragment key={row.label}>
            <dt
              className={cx('t-mono', 't-11', styles.label, hasCyrillic(row.label) && styles.cyr)}
            >
              {row.label}
            </dt>
            <dd className={cx('t-mono', 't-13', styles.value, hasCyrillic(value) && styles.cyr)}>
              {value}
            </dd>
          </Fragment>
        );
      })}
    </dl>
  );
}
